import { skipToken } from '@reduxjs/toolkit/query';
import { InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import { LightTooltip } from 'components/tooltips/LightTooltip';
import { useHourlyRates } from 'hooks/useHourlyRates';
import { CopyHourlyRatesToClipboard } from 'pages/pricelists/CopyHourlyRatesToClipboard';
import { employeeRatesOfCaseList } from 'pages/pricelists/modal/employeePricing';
import { inForceListOn, pricedRoleRows } from 'pages/pricelists/modal/matterPricelistEditorUtils';
import { useToday } from 'pages/pricelists/modal/useEmployeePricing';
import { formatRateRange } from 'pages/pricelists/rateRange';
import { ClipboardRateRow, EMPLOYEE_CLIPBOARD_GROUP, employeeClipboardGroups } from 'pages/pricelists/util';
import { useMemo } from 'react';
import { ClassifierData } from 'types/classifiers';
import { JobRoleGroupType } from 'types/jobRoles';
import { MatterDto } from 'types/matters';
import { CalculatedPricelist, PricelistCardEntry, PricingKind, ScopeType } from 'types/pricelists';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { MatterPricelistsPopover } from './MatterPricelistsPopover';

/**
 * The argument of the read of the case client's lists, skipped while the case has no client, which a draft may lack.
 * A draft's read sends that missing client as a party whose fields are all null, so the id is what is checked.
 */
export const clientPricelistsArg = (matter: Pick<MatterDto, 'clientParty'> | undefined) =>
    matter?.clientParty?.id ? String(matter.clientParty.id) : skipToken;

/** The client's list a case is priced by: the one it references, or else its own list with the case's pricing. */
export const findPricelistForMatter = (
    pricelists: CalculatedPricelist[] | undefined,
    matter: MatterDto
): CalculatedPricelist | undefined => {
    if (!pricelists) return undefined;
    if (matter.pricelist?.id) {
        const directMatch = pricelists.find((pricelist) => pricelist.id === matter.pricelist?.id);
        if (directMatch) return directMatch;
    }
    return pricelists.find(
        (pricelist) => pricelist.scopeTypeCode === ScopeType.MATTER && pricelist.pricingHash === matter.pricingHash
    );
};

interface Props {
    name: string;
    /** Keeps the name on one line, cut short with the whole of it as its title, for a cell of a fixed height. */
    oneLineName?: boolean;
    currencyTypeCode?: string | null;
    // Every list of the case in date order, the base list first; stored, or staged by the case form.
    lists: PricelistCardEntry[];
    openedDate: string | null;
    classifiers: Record<string, ClassifierData>;
    // An employee-priced case's lists are its people, each with the role they hold as a plain label.
    pricingKind?: PricingKind;
    // The lists are the periods of a list the case follows.
    following?: boolean;
}

/**
 * The case's price-list card, on the case page and in the case form: name, the in-force list's range with
 * its average, and behind the info icon a popover with a tab per list. The copy icon beside the range
 * always copies the list in force today, in full: the role groups as everywhere else and the personal
 * rows as a third group, whatever tab the popover may show. An unpriced list shows no range.
 */
export const MatterPricelistCard = ({
    name,
    oneLineName = false,
    currencyTypeCode,
    lists,
    openedDate,
    classifiers,
    pricingKind = PricingKind.ROLE,
    following = false,
}: Props): JSX.Element => {
    const perEmployee = pricingKind === PricingKind.EMPLOYEE;
    const today = useToday();
    const inForceList = inForceListOn(lists, today) ?? lists[0];
    const { hourlyRateDetails } = useHourlyRates(perEmployee ? [] : inForceList?.rates);
    const personalGroup = useMemo(() => {
        if (perEmployee) {
            return employeeClipboardGroups(employeeRatesOfCaseList(inForceList?.personalRates));
        }
        const groups: Record<string, ClipboardRateRow[]> = {};
        if (inForceList && inForceList.personalRates.length > 0) {
            groups.PERSONAL = inForceList.personalRates.map((row) => ({
                name: row.employee.name,
                price: row.hourlyPrice,
            }));
        }
        return groups;
    }, [inForceList, perEmployee]);
    const groupTitles = useMemo(
        () => ({
            [JobRoleGroupType.LEGAL]: getClassifierText(classifiers['JOB_ROLE_GROUP_TYPE'], JobRoleGroupType.LEGAL),
            [JobRoleGroupType.ADMIN]: getClassifierText(classifiers['JOB_ROLE_GROUP_TYPE'], JobRoleGroupType.ADMIN),
            PERSONAL: t('pricelists.case-editor.individual-rates'),
            [EMPLOYEE_CLIPBOARD_GROUP]: t('pricelists.employee.clipboard-group'),
        }),
        [classifiers]
    );
    const isPriced =
        !!inForceList && (pricedRoleRows(inForceList.rates).length > 0 || inForceList.personalRates.length > 0);
    const rateRange =
        isPriced && inForceList
            ? formatRateRange(
                  inForceList.minHourlyPrice,
                  inForceList.maxHourlyPrice,
                  inForceList.avgHourlyPrice,
                  currencyTypeCode
              )
            : '';

    return (
        <div className={cn('text-grey-600 flex flex-col', oneLineName && 'min-w-0')}>
            {name ? (
                <div
                    className={cn('mr-2 font-medium', oneLineName && 'truncate')}
                    title={oneLineName ? name : undefined}
                >
                    {name}
                </div>
            ) : null}
            <div className="flex items-center whitespace-nowrap">
                <LightTooltip
                    mode="click"
                    // An unpriced list shows the glyph alone, which names nothing.
                    label={
                        rateRange
                            ? t('matters.pricelist-show-rates-range', { range: rateRange })
                            : t('matters.pricelist-show-rates')
                    }
                    title={
                        <MatterPricelistsPopover
                            lists={lists}
                            openedDate={openedDate}
                            currencyTypeCode={currencyTypeCode}
                            pricingKind={pricingKind}
                            following={following}
                        />
                    }
                >
                    <span className="text-link flex cursor-pointer items-center hover:underline">
                        {rateRange}
                        <InfoOutlinedIcon className="text-grey-400 ml-px h-[1.2rem]" />
                    </span>
                </LightTooltip>
                <CopyHourlyRatesToClipboard
                    data={perEmployee ? {} : hourlyRateDetails}
                    groupTitles={groupTitles}
                    extraGroups={personalGroup}
                />
            </div>
        </div>
    );
};
